import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { invoiceLocaleToLanguage } from '@features/invoices/constants/invoiceLocaleToLanguage'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'

import type { TradeName } from '../../../types/companyValues'
import type { InvoicingSettingsPreviewPatch } from '../../../types/invoicingSettingsPreviewPatch'
import type { PresetsSettingsFormValues } from '../types/presetsSettingsFormValues'

type PresetsPreviewValues = Pick<
  PresetsSettingsFormValues,
  'contactEmail' | 'contactFirstName' | 'contactLastName' | 'contactPhone' | 'currency' | 'language'
>

interface GetPresetsPreviewPatchOptions {
  savedValues: PresetsPreviewValues
  tradeNames: TradeName[]
  values: PresetsPreviewValues
}

const normalizeContactValue = (value?: string) => value || ''

// The preview always renders an invoice draft. Language and currency are invoice model defaults.
// The contact person is the sender, taken from the primary trade name.
// Item type is saved on the invoice model, but changing it does not change the preview.
export const getPresetsPreviewPatch = (
  options: GetPresetsPreviewPatchOptions,
): InvoicingSettingsPreviewPatch | undefined => {
  const { savedValues, tradeNames, values } = options
  const hasCurrencyChanged = values.currency !== savedValues.currency
  const hasLanguageChanged = values.language !== savedValues.language
  const contactFields = ['contactEmail', 'contactFirstName', 'contactLastName', 'contactPhone'] as const
  const hasContactChanged = contactFields.some(
    (field) => normalizeContactValue(values[field]) !== normalizeContactValue(savedValues[field]),
  )

  if (!hasCurrencyChanged && !hasLanguageChanged && !hasContactChanged) {
    return undefined
  }

  const companyValues = { company: { tradeNames } }

  if (!hasCurrencyChanged && !hasLanguageChanged) {
    return { settings: { values: companyValues } }
  }

  return {
    settings: {
      modelPatches: {
        [InvoiceDocumentType.Invoice]: {
          currency: { default: values.currency },
          language: { default: invoiceLocaleToLanguage[values.language] || DEFAULT_INVOICE_LANGUAGE },
        },
      },
      ...(hasContactChanged ? { values: companyValues } : {}),
    },
  }
}
